import OpenAI from "openai";
import { getOpenAIApiKey } from "../config/runtime-config";

// Initialize OpenAI client with fallback API key
export const openai = new OpenAI({
  apiKey: getOpenAIApiKey() || "dummy-key",
  dangerouslyAllowBrowser: true, // Only for development
});

// Function to reinitialize OpenAI client with new API key
export function reinitializeOpenAI(apiKey?: string) {
  const key = apiKey || getOpenAIApiKey();
  if (key && key !== "dummy-key") {
    return new OpenAI({
      apiKey: key,
      dangerouslyAllowBrowser: true,
    });
  }
  return openai;
}

// Custom instructions configuration
const customInstructions = {
  formatting: {
    allowedTags: ["h2", "h3", "p", "b", "ol", "ul", "li"],
    lineBreaks: true,
    listTypes: ["ordered", "unordered"],
  },
  style: {
    useOxfordComma: true,
    useActiveVoice: true,
    maxSentencesPerParagraph: 3,
  },
  avoidPhrases: [
    "in the rapidly evolving world",
    "in today's world",
    "accordingly",
    "additionally",
    "robust",
    "invaluable",
    "realm",
    "notwithstanding",
    "thus",
    "adept",
    "commendable",
    "deliv",
    "increasingly",
    "Realm",
    "Exciting",
    "commendable",
    "indeed",
    "certainly",
    "consequently",
    "hense",
  ],
  avoidCharacters: ["--", "😀", "😊", "👍", "✨"],
  contentGuidelines: {
    usePracticalInsights: true,
    includeExamples: true,
    includeData: true,
    requireSources: true,
    engageReader: true,
    optimizeForSearch: {
      seo: true,
      aeo: true,
      voice: true,
      geo: true,
    },
  },
};

// Function to generate system prompt with custom instructions
function getSystemPrompt(role: string): string {
  return `You are a professional ${role} focused on providing clear, actionable insights.

Content Requirements:
- Use HTML tags: ${customInstructions.formatting.allowedTags.join(", ")}
- Include frequent line breaks for readability
- Use the Oxford comma in lists
- Write in active voice
- Keep paragraphs to ${customInstructions.style.maxSentencesPerParagraph} sentences or less
- Support claims with specific examples, personal anecdotes, or data
- Never make up or fabricate data - only use verifiable sources
- Always cite sources for data and statistics
- Address readers directly using 'you' and 'your'
- Pose thought-provoking questions to engage readers

Search Optimization:
- Structure content for SEO best practices
- Optimize for voice search with natural language
- Include location-relevant context when applicable
- Structure answers for featured snippets
- Use semantic HTML for better accessibility
- Implement proper heading hierarchy
- Include relevant internal linking opportunities
- Optimize for mobile readability

Avoid:
- HTML head and doctype declarations
- Emojis and special characters
- Double dashes (use single dash instead)
- Clichés and metaphors
- Broad generalizations
- Introductory phrases like 'in conclusion' or 'in summary'
- Excessive adverbs and adjectives
- Passive voice constructions
- Making up or fabricating data
- The following words: ${customInstructions.avoidPhrases.join(", ")}

Focus on:
- Practical and actionable insights
- Specific examples and case studies
- Data-backed statements with sources
- Clear, direct communication
- Engaging questions and discussion points
- Geographic relevance when applicable
- Voice search optimization
- Featured snippet optimization
- Mobile-first readability
- Semantic structure`;
}

// Function to generate blog content
export async function generateBlogContent(prompt: string, context?: string) {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      return "OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your .env file.";
    }

    const completion = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content: getSystemPrompt("blog post writer"),
        },
        ...(context
          ? [
              {
                role: "user",
                content: `Previous context: ${context}`,
              },
            ]
          : []),
        {
          role: "user",
          content: prompt,
        },
      ],
      temperature: 0.7,
      max_tokens: 2000,
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error("Error generating blog content:", error);
    throw error;
  }
}

// Function to generate blog title suggestions
export async function generateTitleSuggestions(content: string) {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      return [
        "OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your .env file.",
      ];
    }

    const completion = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content:
            getSystemPrompt("title writer") +
            "\n\nGenerate 5 engaging, SEO-friendly title suggestions. Each title should be clear, compelling, and accurately reflect the content. Optimize for both search engines and human readers. Avoid clichés, buzzwords, and clickbait.",
        },
        {
          role: "user",
          content: content,
        },
      ],
      temperature: 0.7,
      max_tokens: 200,
    });

    // Split the response into lines and clean up each title
    const titles = completion.choices[0].message.content
      ?.split("\n")
      .map((title) => title.trim())
      .filter((title) => title.length > 0)
      .map((title) => {
        // Remove any numbers, quotes, or dashes from the start
        return title.replace(/^[0-9."'-\s]+/, "").trim();
      });

    return titles || [];
  } catch (error) {
    console.error("Error generating title suggestions:", error);
    throw error;
  }
}

// Function to generate meta description
export async function generateMetaDescription(content: string) {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      return "OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your .env file.";
    }

    const completion = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content:
            getSystemPrompt("SEO specialist") +
            "\n\nCreate a compelling meta description between 150-160 characters that includes relevant keywords, optimizes for click-through rate, and accurately summarizes the content. Consider voice search patterns and local SEO when applicable.",
        },
        {
          role: "user",
          content: content,
        },
      ],
      temperature: 0.7,
      max_tokens: 100,
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error("Error generating meta description:", error);
    throw error;
  }
}

// Function to suggest improvements
export async function suggestImprovements(content: string) {
  try {
    // Check if API key is available
    if (!import.meta.env.VITE_OPENAI_API_KEY) {
      return "OpenAI API key not configured. Please add VITE_OPENAI_API_KEY to your .env file.";
    }

    const completion = await openai.chat.completions.create({
      model: "gpt-4-turbo-preview",
      messages: [
        {
          role: "system",
          content:
            getSystemPrompt("content editor") +
            `

<h2>Content Analysis</h2>

Analyze this content and provide specific improvements for:

<h3>Structure and Readability</h3>
<ul>
<li>Section organization and flow</li>
<li>Heading hierarchy and semantic structure</li>
<li>Sentence clarity and paragraph length</li>
<li>Mobile readability optimization</li>
</ul>

<h3>Search Optimization</h3>
<ul>
<li>SEO best practices</li>
<li>Voice search optimization</li>
<li>Featured snippet potential</li>
<li>Local SEO elements</li>
</ul>

<h3>User Engagement</h3>
<ul>
<li>Opening hook effectiveness</li>
<li>Reader interaction points</li>
<li>Call-to-action placement and clarity</li>
<li>Internal linking strategy</li>
</ul>

<h3>Technical Elements</h3>
<ul>
<li>Data accuracy and citations</li>
<li>Semantic HTML usage</li>
<li>Accessibility considerations</li>
<li>Mobile responsiveness</li>
</ul>

Provide actionable recommendations with specific examples.`,
        },
        {
          role: "user",
          content: content,
        },
      ],
      temperature: 0.7,
      max_tokens: 500,
    });

    return completion.choices[0].message.content;
  } catch (error) {
    console.error("Error generating improvement suggestions:", error);
    throw error;
  }
}
